/**
 * AI 命令 - 管理 AI 功能和配置
 */

import { Command } from 'commander';
import { withErrorHandling } from '../utils/error-handler';
import { AIConfigManager } from '../config/ai-config';
import { colors } from '../utils/colors';
import { Table } from 'console-table-printer';
import ora from 'ora';
import path from 'path';
import fs from 'fs-extra';
import { SmartAgentAllocator } from '../core/smart-agent-allocator';
import { logger } from '../utils/logger';

export function createAICommand(): Command {
  const command = new Command('ai');
  
  command
    .description('Manage AI features and configuration')
    .addCommand(createAIStatusCommand())
    .addCommand(createAIEnableCommand())
    .addCommand(createAIDisableCommand())
    .addCommand(createAIModeCommand())
    .addCommand(createAITestCommand())
    .addCommand(createAIStatsCommand());
  
  return command;
}

/**
 * ai status - 显示 AI 功能状态
 */
function createAIStatusCommand(): Command {
  const command = new Command('status');
  
  command
    .description('Show AI features status and configuration')
    .action(withErrorHandling(async () => {
      const configManager = new AIConfigManager();
      const config = configManager.getConfig();
      
      console.log(colors.header('\n🤖 AI Features Status\n'));
      
      const statusTable = new Table({
        columns: [
          { name: 'feature', title: 'Feature', alignment: 'left' },
          { name: 'status', title: 'Status', alignment: 'center', colored: true },
          { name: 'details', title: 'Details', alignment: 'left' }
        ]
      });
      
      // AI 模式
      statusTable.addRow({
        feature: 'AI Mode',
        status: config.mode.toUpperCase(),
        details: getAIModeDescription(config.mode)
      });
      
      // Memory 系统
      statusTable.addRow({
        feature: 'Memory System',
        status: config.memory.enabled ? 'ENABLED' : 'DISABLED',
        details: config.memory.enabled 
          ? `User: ${config.memory.userId || 'not set'}, Project: ${config.memory.projectId || 'not set'}`
          : 'Not configured'
      });
      
      // Agent 分配
      statusTable.addRow({
        feature: 'Agent Allocation',
        status: config.defaults.autoAssignAgents ? 'AUTO' : 'MANUAL',
        details: `Min confidence: ${config.agentAllocation.minConfidence * 100}%`
      });
      
      // 新任务
      statusTable.addRow({
        feature: 'New Tasks',
        status: config.defaults.enableForNewTasks ? 'AI-ENABLED' : 'STANDARD',
        details: 'AI features for new task creation'
      });
      
      // PRD 生成
      statusTable.addRow({
        feature: 'PRD Generation',
        status: config.defaults.enableForPRD ? 'AI-ENHANCED' : 'STANDARD',
        details: 'AI features for PRD generation'
      });
      
      statusTable.printTable();
      
      // 环境变量
      console.log(colors.header('\n🔧 Environment Variables:\n'));
      console.log(`WORKFLOW_ENABLE_MEMORY: ${process.env.WORKFLOW_ENABLE_MEMORY || 'not set'}`);
      console.log(`WORKFLOW_AI_MODE: ${process.env.WORKFLOW_AI_MODE || 'not set'}`);
      
      // 提示
      console.log(colors.header('\n💡 Tips:\n'));
      console.log('• Use ' + colors.command('workflow ai enable') + ' to enable memory system');
      console.log('• Use ' + colors.command('workflow ai mode <mode>') + ' to change AI mode');
      console.log('• Use ' + colors.command('workflow ai test') + ' to test AI features');
    }));
  
  return command;
}

/**
 * ai enable - 启用 AI 功能
 */
function createAIEnableCommand(): Command {
  const command = new Command('enable');
  
  command
    .description('Enable AI features and memory system')
    .option('--user-id <id>', 'User ID for memory system')
    .option('--memory-only', 'Only enable memory system', false)
    .action(withErrorHandling(async (options) => {
      const configManager = new AIConfigManager();
      
      // 获取项目 ID
      let projectId: string | undefined;
      const configPath = path.join(process.cwd(), '.workflow', 'config', 'project.json');
      if (await fs.pathExists(configPath)) {
        const config = await fs.readJSON(configPath);
        projectId = config.id;
      }
      
      const spinner = ora('Enabling AI features...').start();
      
      try {
        if (options.memoryOnly) {
          await configManager.enableMemory(
            options.userId || process.env.USER,
            projectId
          );
          spinner.succeed('Memory system enabled');
        } else {
          await configManager.updateConfig({
            mode: 'memory-enhanced',
            memory: {
              enabled: true,
              userId: options.userId || process.env.USER,
              projectId: projectId,
            },
            defaults: {
              enableForNewTasks: true,
              enableForPRD: true,
              autoAssignAgents: true,
            }
          });
          spinner.succeed('AI features enabled');
        }
        
        console.log(colors.success('\n✅ AI features are now active!'));
        console.log(colors.info('\nWhat\'s enabled:'));
        console.log('• Memory system for intelligent suggestions');
        console.log('• Smart agent allocation based on task type');
        console.log('• AI-enhanced PRD generation');
        console.log('• Task pattern learning');
        
      } catch (error) {
        spinner.fail('Failed to enable AI features');
        throw error;
      }
    }));
  
  return command;
}

/**
 * ai disable - 禁用 AI 功能
 */
function createAIDisableCommand(): Command {
  const command = new Command('disable');
  
  command
    .description('Disable AI features')
    .option('--memory-only', 'Only disable memory system', false)
    .action(withErrorHandling(async (options) => {
      const configManager = new AIConfigManager();
      
      const spinner = ora('Disabling AI features...').start();
      
      try {
        if (options.memoryOnly) {
          await configManager.disableMemory();
          spinner.succeed('Memory system disabled');
        } else {
          await configManager.updateConfig({
            mode: 'manual',
            memory: {
              enabled: false,
            },
            defaults: {
              enableForNewTasks: false,
              enableForPRD: false,
              autoAssignAgents: false,
            }
          });
          spinner.succeed('AI features disabled');
        }
        
        console.log(colors.warning('\n⚠️  AI features are now disabled'));
        console.log(colors.info('You can re-enable them anytime with ' + colors.command('workflow ai enable')));
        
      } catch (error) {
        spinner.fail('Failed to disable AI features');
        throw error;
      }
    }));
  
  return command;
}

/**
 * ai mode - 设置 AI 模式
 */
function createAIModeCommand(): Command {
  const command = new Command('mode');
  
  command
    .description('Set AI operation mode')
    .argument('[mode]', 'AI mode: auto, manual, hybrid, memory-enhanced')
    .action(withErrorHandling(async (mode?: string) => {
      const configManager = new AIConfigManager();
      
      if (!mode) {
        // 显示当前模式
        const config = configManager.getConfig();
        console.log(colors.header('\n🎯 Current AI Mode\n'));
        console.log(`Mode: ${colors.success(config.mode)}`);
        console.log(`Description: ${getAIModeDescription(config.mode)}`);
        
        console.log(colors.header('\n📋 Available Modes:\n'));
        const modes = ['auto', 'manual', 'hybrid', 'memory-enhanced'];
        modes.forEach(m => {
          console.log(`• ${colors.command(m)} - ${getAIModeDescription(m)}`);
        });
        
        return;
      }
      
      // 验证模式
      const validModes = ['auto', 'manual', 'hybrid', 'memory-enhanced'];
      if (!validModes.includes(mode)) {
        console.log(colors.error(`Invalid mode: ${mode}`));
        console.log('Valid modes: ' + validModes.join(', '));
        return;
      }
      
      await configManager.setMode(mode as any);
      console.log(colors.success(`\n✅ AI mode set to: ${mode}`));
      console.log(`Description: ${getAIModeDescription(mode)}`);
    }));
  
  return command;
}

/**
 * ai test - 测试 AI 功能
 */
function createAITestCommand(): Command {
  const command = new Command('test');
  
  command
    .description('Test AI features and memory system')
    .argument('[task]', 'Task description to test with', 'Implement user authentication with JWT')
    .action(withErrorHandling(async (taskDescription: string) => {
      const configManager = new AIConfigManager();
      const config = configManager.getConfig();
      
      console.log(colors.header('\n🧪 Testing AI Features\n'));
      console.log(`Test task: "${taskDescription}"`);
      
      // 测试 Agent 分配
      console.log(colors.header('\n1. Agent Allocation Test:\n'));
      
      try {
        const allocator = new SmartAgentAllocator({
          enableMemory: config.memory.enabled,
          userId: config.memory.userId,
          projectId: config.memory.projectId,
        });
        
        const testTask = {
          id: 'test-task',
          content: taskDescription,
          status: 'todo' as const,
          priority: 'high' as const,
          type: 'development',
        };
        
        const allocation = await allocator.allocateAgents(testTask);
        
        console.log(`Primary Agent: ${colors.success(allocation.primary)}`);
        if (allocation.reviewers) {
          console.log(`Reviewers: ${allocation.reviewers.join(', ')}`);
        }
        if (allocation.reason) {
          console.log(`Reason: ${allocation.reason}`);
        }
        if (allocation.confidence) {
          console.log(`Confidence: ${allocation.confidence * 100}%`);
        }
        console.log(`Memory-based: ${allocation.memoryBased ? 'Yes' : 'No'}`);
        
      } catch (error) {
        console.log(colors.error('Agent allocation test failed:'), error.message);
      }
      
      // 测试 Memory 系统
      if (config.memory.enabled) {
        console.log(colors.header('\n2. Memory System Test:\n'));
        
        try {
          // 这里可以添加 Memory 系统的测试
          console.log(colors.success('Memory system is configured and ready'));
          console.log(`User ID: ${config.memory.userId}`);
          console.log(`Project ID: ${config.memory.projectId || 'not set'}`);
        } catch (error) {
          console.log(colors.error('Memory system test failed:'), error.message);
        }
      } else {
        console.log(colors.warning('\n⚠️  Memory system is not enabled'));
        console.log('Enable it with: ' + colors.command('workflow ai enable'));
      }
      
      console.log(colors.header('\n✅ AI features test completed'));
    }));
  
  return command;
}

/**
 * ai stats - 显示 AI 统计信息
 */
function createAIStatsCommand(): Command {
  const command = new Command('stats');
  
  command
    .description('Show AI and agent performance statistics')
    .action(withErrorHandling(async () => {
      const configManager = new AIConfigManager();
      const config = configManager.getConfig();
      
      if (!config.memory.enabled) {
        console.log(colors.warning('Memory system is not enabled'));
        console.log('Enable it with: ' + colors.command('workflow ai enable'));
        return;
      }
      
      const spinner = ora('Loading AI statistics...').start();
      
      try {
        const allocator = new SmartAgentAllocator({
          enableMemory: true,
          userId: config.memory.userId,
          projectId: config.memory.projectId,
        });
        
        const stats = await allocator.getAllocationStats();
        
        spinner.stop();
        
        console.log(colors.header('\n📊 AI Performance Statistics\n'));
        
        if (stats.error || stats.message) {
          console.log(colors.warning(stats.error || stats.message));
          return;
        }
        
        const table = new Table({
          columns: [
            { name: 'agent', title: 'Agent', alignment: 'left' },
            { name: 'tasks', title: 'Total Tasks', alignment: 'center' },
            { name: 'success', title: 'Success Rate', alignment: 'center', colored: true },
            { name: 'avgTime', title: 'Avg Time', alignment: 'center' },
            { name: 'specialties', title: 'Top Specialties', alignment: 'left', maxLen: 30 }
          ]
        });
        
        Object.entries(stats).forEach(([agentName, agentStats]: [string, any]) => {
          if (agentStats.totalTasks > 0) {
            table.addRow({
              agent: agentName,
              tasks: agentStats.totalTasks,
              success: `${Math.round(agentStats.successRate * 100)}%`,
              avgTime: agentStats.averageTime ? `${Math.round(agentStats.averageTime / 1000)}s` : '-',
              specialties: agentStats.specialties?.slice(0, 3).join(', ') || '-'
            });
          }
        });
        
        if (table.table.rows.length > 0) {
          table.printTable();
        } else {
          console.log(colors.info('No statistics available yet'));
          console.log('Statistics will be collected as you use AI features');
        }
        
      } catch (error) {
        spinner.fail('Failed to load statistics');
        logger.error('Failed to get AI stats:', error);
        throw error;
      }
    }));
  
  return command;
}

/**
 * 获取 AI 模式描述
 */
function getAIModeDescription(mode: string): string {
  switch (mode) {
    case 'auto':
      return 'Automatic AI assistance based on rules';
    case 'manual':
      return 'AI features disabled, manual control only';
    case 'hybrid':
      return 'AI suggestions with manual confirmation';
    case 'memory-enhanced':
      return 'Full AI with memory-based learning';
    default:
      return 'Unknown mode';
  }
}